import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Heart, ShieldCheck, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { products } from '../mock';

const iconMap = { Truck, Heart, ShieldCheck, UserCircle };

const ProductLogo = ({ product }) => {
  const Icon = iconMap[product.icon];

  if (product.image) {
    return (
      <div className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden">
        <img
          src={product.image}
          alt={`${product.name} logo`}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.color} flex items-center justify-center shadow-lg`}
    >
      {Icon ? (
        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
      ) : (
        <span className="text-white font-black text-2xl">{product.icon}</span>
      )}
    </div>
  );
};

const Ecosystem = ({ onGetStarted }) => {
  return (
    <section id="products" className="relative py-20">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-[13px] font-semibold bg-gradient-to-r from-[#a855f7] to-[#60a5fa] bg-clip-text text-transparent mb-3">
            Our Ecosystem
          </div>
          <h2 className="text-white text-[36px] lg:text-[42px] font-bold tracking-tight">
            Powerful Products. One Mission.
          </h2>
          <p className="text-gray-400 text-[15px] mt-3">
            Innovative platforms built to solve real problems and drive real impact.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
          {products.map((product) => (
            <div
              key={product.id}
              className="group relative rounded-2xl bg-[#0d0d16]/70 border border-white/5 p-5 hover:border-[#a855f7]/40 hover:bg-[#12121d]/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(124,58,237,0.25)]"
            >
              <div className="flex flex-col items-center text-center">
                <ProductLogo product={product} />
                <div className="text-white font-semibold text-[15px] mt-4">{product.name}</div>
                <div className="text-gray-400 text-[11.5px] mt-2 leading-relaxed min-h-[54px]">
                  {product.description}
                </div>
                {product.id === 'rapidreps' ? (
                  <Link
                    to="/rapidreps-support"
                    data-testid="product-learn-more-rapidreps"
                    className="text-[11.5px] text-[#a855f7] mt-3 hover:text-[#c084fc] transition-colors"
                  >
                    Learn More →
                  </Link>
                ) : (
                  <button
                    onClick={onGetStarted}
                    data-testid={`product-learn-more-${product.id}`}
                    className="text-[11.5px] text-[#a855f7] mt-3 hover:text-[#c084fc] transition-colors bg-transparent"
                  >
                    Learn More →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <Button
            variant="outline"
            onClick={onGetStarted}
            data-testid="view-all-products-btn"
            className="h-10 px-5 rounded-md text-[13px] bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:text-white"
          >
            View All Products ↗
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
